import React from "react";
import { Link } from "react-router-dom";

import TopStrip from "../components/TopStrip";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import HelpButton from "../components/HelpButton";
import TestimonialsCarousel from "../components/TestimonialsCarousel";

import heroBanner from "../assets/images/hero-images/devsecops.webp";
import heroMobile from "../assets/images/mobile-banners/devsecops-services-mobile.webp";
import flip1 from "../assets/images/flip-cards/1.webp";
import flip2 from "../assets/images/flip-cards/2.webp";
import flip3 from "../assets/images/flip-cards/3.webp";
import flip4 from "../assets/images/flip-cards/4.webp";

const securityCards = [
  {
    delay: "100",
    title: "Automated Vulnerability Scanning",
    desc: "We don't wait for audits. We implement automated SAST and DAST scanning in your pipeline, detecting code vulnerabilities and weaknesses before they ever reach production.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="#0dcaf0"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
        />
      </svg>
    ),
  },
  {
    delay: "200",
    title: "Data Encryption & DLP",
    desc: "Your data is locked down. We configure end-to-end encryption (at rest and in transit) and implement Data Loss Prevention (DLP) protocols to stop leaks at the source.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 64 64"
        fill="#0dcaf0"
      >
        <rect width="32" height="24" x="16" y="20" rx="2" />
        <path d="M32 28a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM24 20v-4a8 8 0 0 1 16 0v4" />
      </svg>
    ),
  },
  {
    delay: "300",
    title: "Compliance as Code",
    desc: "GDPR, HIPAA, or PCI-DSS? We map compliance requirements directly into your infrastructure scripts, ensuring your environment remains audit-ready 24/7/365.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="#0dcaf0"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  {
    delay: "400",
    title: "Identity & Access Management (IAM)",
    desc: "Stop unauthorized access. We implement strict Least Privilege Access controls and Multi-Factor Authentication (MFA) to ensure only the right people touch your production servers.",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="#0dcaf0"
        strokeWidth="2"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
        />
      </svg>
    ),
  },
];

const addons = [
  {
    title: "SECURE PRIVATE TUNNEL",
    price: "$9 / mo",
    color: "rgb(14,209,179)",
    items: [
      "Ultra-Fast Connection",
      "Secure Connectivity",
      "Data Encryption",
      "Multiple Devices Setup",
      "No-logs No-Restrictions policy",
      "Offshore Privacy Protection",
      "Virtual Private Network",
      "Multiple Region",
      "Dedicated IP Address",
      "1Gbps Connection",
      "24/7 Support",
    ],
    btn: "Get Started",
  },
  {
    title: "SECURE DNS",
    price: "$99 / mo",
    color: "rgb(108,132,238)",
    items: [
      "Secure and Cost-Effective",
      "Multiple Geolocation Targets",
      "Dynamic DNS",
      "EDNS-client-subnet support",
      "DDoS Protected DNS",
      "100M Queries per month",
      "Web Redirects",
      "Search Engine Friendly",
      "Flexible and Scalable",
      "Statistics and DNS Branding",
      "24/7 Support",
    ],
    btn: "Inquire Now",
  },
  {
    title: "APPLICATION FIREWALL",
    price: "$199 / mo",
    color: "rgb(238,108,218)",
    items: [
      "Smart Application Security",
      "Built-in DDOS mitigation",
      "Global CDN Support",
      "Image & Mobile optimization",
      "Reporting and Analytics",
      "Load Balancing",
      "Dedicated SSL",
      "Rate Limit & Page Rules",
      "Protect applications, containers, VMs",
      "Protect APIs & Microservices",
      "24/7 Support",
    ],
    btn: "Inquire Now",
  },
];

const flipCards = [
  {
    front: "Rapid Integration",
    frontDesc:
      "Security shouldn't delay your launch. Our WAF and shielding protocols can be deployed in under 30 minutes with zero downtime to your live traffic.",
    back: "Quick Implementation",
    img: flip1,
  },
  {
    front: "Deep Packet Inspection (DPI)",
    frontDesc:
      " We don't just look at headers. We inspect the payload of every packet, filtering out malicious requests while ensuring legitimate users pass through instantly.",
    back: "Deep Inspection",
    img: flip2,
  },
  {
    front: "AI-Driven Threat Detection",
    frontDesc:
      "Static firewalls rules are not enough. Our AI models analyze traffic patterns in real-time to detect zero-day exploits and sophisticated botnet attacks",
    back: "AI Traffic Filtering",
    img: flip3,
  },
  {
    front: "Global Mitigation Centers",
    frontDesc:
      "DDoS resilience requires scale. Our distributed mitigation centers across global regions absorb massive attack traffic, keeping your origin server safe and responsive.",
    back: "Multiple Networks",
    img: flip4,
  },
];

function DevSecOpsServices() {
  return (
    <>
      <TopStrip />
      <Navbar />

      {/* HERO */}
      <section style={{ marginTop: "125px" }}>
        <div className="container-fluid p-0">
          <img
            src={heroBanner}
            width="1600"
            height="520"
            alt="DevSecOps Services"
            className="img-fluid w-100 d-none d-md-block"
          />
          <img
            src={heroMobile}
            width="768"
            height="1000"
            alt="DevSecOps mobile"
            className="img-fluid w-100 d-md-none d-block"
          />
        </div>
      </section>

      {/* 50 YEARS SECTION */}
      <div className="container py-5 my-5 overflow-hidden">
        <div className="row align-items-center gy-4" data-aos="fade-up">
          <div className="col-lg-6 col-md-6 col-sm-12 d-flex flex-column align-items-center text-center">
            <div className="d-flex flex-sm-row flex-column align-items-center justify-content-center mb-3">
              <span
                className="txt-color fw-bold me-3"
                style={{ fontSize: "6.5rem", lineHeight: 1 }}
              >
                50+
              </span>
              <div
                className="text-start text-uppercase small fw-500"
                style={{ opacity: 0.6, lineHeight: 1.4 }}
              >
                <strong>Years</strong>
                <br />
                Of IT
                <br />
                Security
              </div>
            </div>
            <div style={{ fontSize: "1.5rem", opacity: 0.8 }}>
              Evolved into <span className="txt-color fw-bold">DevSecOps</span>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-12 position-relative ps-lg-5">
            <div className="vertical-line d-none d-md-block"></div>
            <h3 className="fs-4 fw-semibold mb-3" style={{ opacity: 0.8 }}>
              Security is no longer a final hurdle; it is the foundation. We
              have shifted security 'left,' embedding automated threat detection
              and compliance checks into every line of code before it ever
              reaches production. No bottlenecks. No surprises. Just secure,
              compliant releases—every single time.
            </h3>
            <p>
              We recommend getting yourself protected right from the start to
              meet security expectations, from Agile to DevOps to DevSecOps. The
              Next Evolution to put a strong focus on the needs of your IT
              security to figure out the best possible solutions that fit your
              demand.
            </p>
          </div>
        </div>
      </div>

      {/* STATS */}
      <section className="bg-info-subtle py-3 my-5" data-aos="fade-left">
        <div className="container">
          <div className="row text-center">
            {[
              { n: "1,190", l: "MANAGED CLIENTS" },
              { n: "91+", l: "SECURITY AUDITS" },
              { n: "47", l: "CERTIFIED ENGINEERS" },
              { n: "101", l: "THREATS NEUTRALIZED DAILY" },
            ].map((s, i) => (
              <div className="col-md-3 mb-4 mb-md-0 position-relative" key={i}>
                <h2 className="fw-bold text-info display-5 mb-0">{s.n}</h2>
                <p className="text-uppercase mt-2 fw-semibold">{s.l}</p>
                {i < 3 && (
                  <div
                    className="d-none d-md-block position-absolute top-0 end-0 h-100"
                    style={{ width: "1px", backgroundColor: "gray" }}
                  ></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECURITY ARCHITECTURE CARDS */}
      <section className="py-5">
        <div className="container">
          <h2 className="text-center top-line ">
            The Security <span className="txt-color">Architecture.</span>
          </h2>
          <h4 className="text-center mb-5">
            Four pillars of our Zero-Trust methodology.
          </h4>
          <div className="row g-4">
            {securityCards.map((c, i) => (
              <div
                className="col-md-6 col-lg-6"
                key={i}
                data-aos="fade-up"
                data-aos-delay={c.delay}
              >
                <div className="p-4 rounded shadow-sm h-100 service-card text-center text-md-start">
                  <div className="d-flex align-items-center mb-3">
                    {c.icon}
                    <h5 className="fw-bold text-info m-0 ms-3">{c.title}</h5>
                  </div>
                  <p className="text-muted mb-0">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* END-TO-END SECURITY */}
      <section className="bg-light py-5 mt-4">
        <div className="container text-center mb-4">
          <h2 data-aos="fade-right" data-aos-delay="200">
            End-to-End Security.{" "}
            <span className="txt-color">Zero Compromise.</span>
          </h2>
        </div>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <p
                className="fst-italic fw-semibold text-muted text-center"
                data-aos="fade-left"
                data-aos-delay="300"
              >
                "We don't just patch holes; we build fortresses. Our 'Security
                as a Service' model aligns strictly with the OWASP Top 10 to
                neutralize threats like SQL Injection, XSS, and DDoS attacks
                before they ever touch your infrastructure. From the network
                edge to the application core, we deploy a multi-layered defense
                strategy that evolves faster than the hackers do."
              </p>
            </div>
          </div>
          <div className="row justify-content-between align-items-center mt-5">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <ul
                className="list-unstyled ps-3"
                data-aos="fade-right"
                data-aos-delay="400"
              >
                <li className="mb-3">✔️ Network and Infrastructure Security</li>
                <li className="mb-3">✔️ System and Endpoint Security</li>
                <li className="mb-3">✔️ Web Application Firewall</li>
                <li className="mb-3">✔️ Ransomware Protection</li>
                <li className="mb-3">✔️ Data encryption and Worm storage</li>
                <li className="mb-3">
                  ✔️ Gbps DDOS Protection against millions of packets per second
                </li>
                <li className="mb-3">
                  ✔️ UDP, TCP, ICMP, SYN, and HTTP Flood Protection
                </li>
                <li className="mb-3">
                  ✔️ Protection against HTTP/HTTPS, XML RPC, Bandwidth Leeching,
                  Botnet etc
                </li>
                <li className="mb-3">✔️ Security Audit and Compliance</li>
              </ul>
            </div>
            <div
              className="col-lg-5 col-md-6 col-sm-12 d-flex justify-content-center mt-4 mt-md-0"
              data-aos="fade-left"
              data-aos-delay="500"
            >
              <Link
                to="/contact"
                className="btn btn-info fw-bold text-white px-4 py-2"
              >
                Talk to Security Architect
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FLIP CARDS */}
      <section className="py-5 my-4">
        <div className="container">
          <div className="row g-4">
            {flipCards.map((c, i) => (
              <div className="col-md-6 col-lg-3" key={i}>
                <div className="flip-card">
                  <div className="flip-card-inner">
                    <div className="flip-card-front d-flex flex-column justify-content-center align-items-center p-4 text-center bg-light">
                      <h2 className="fw-bold text-info">{c.front}</h2>
                      <p className="text-muted small mt-2">{c.frontDesc}</p>
                    </div>
                    <div className="flip-card-back position-relative">
                      <img
                        src={c.img}
                        loading="lazy"
                        className="w-100 h-100 object-fit-cover"
                        alt={c.back}
                      />
                      <div className="position-absolute top-50 start-50 translate-middle text-center">
                        <h2 className="fw-bold text-white">{c.back}</h2>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADDONS */}
      <section className="py-5">
        <div className="container">
          <h2 className="text-center top-line mb-5">DDevOps Security Addons</h2>
          <div className="row g-4">
            {addons.map((a, i) => (
              <div
                className="col-lg-4 col-md-6"
                key={i}
                data-aos="fade-up"
                data-aos-delay={100 * (i + 1)}
              >
                <div className="border rounded shadow-sm h-100 d-flex flex-column">
                  <div
                    className="text-white text-center p-4"
                    style={{ backgroundColor: a.color }}
                  >
                    <h5 className="fw-bold m-0">{a.title}</h5>
                    <h6 className="fw-bold mt-1 mb-0">{a.price}</h6>
                  </div>
                  <ul
                    className="p-4 mb-0 flex-grow-1 d-flex flex-column"
                    style={{ listStyle: "none", paddingLeft: 0 }}
                  >
                    {a.items.map((item, j) => (
                      <li className="mb-3" key={j}>
                        {item}
                      </li>
                    ))}
                    <li
                      className="mt-auto text-center"
                      style={{ listStyle: "none" }}
                    >
                      <Link
                        to="/contact"
                        className="btn btn-info text-white mt-3"
                      >
                        {a.btn}
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsCarousel />
      <Footer />
      <HelpButton />
    </>
  );
}

export default DevSecOpsServices;
